import React, { useState, useRef } from 'react';
import { 
  Upload, FileText, Image as ImageIcon, Trash2, Download, 
  ExternalLink, File, CheckCircle2, AlertCircle, Search
} from 'lucide-react';
import { Patient, PatientFile } from '../types';

interface PatientFilesProps {
  patient: Patient;
  onUpdatePatient: (patient: Patient) => void;
}

const PatientFiles: React.FC<PatientFilesProps> = ({ patient, onUpdatePatient }) => {
  const [files, setFiles] = useState<PatientFile[]>(() => patient.files || []);
  const [dragActive, setDragActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper to format file size
  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const processFile = (file: File) => {
    // Validate file size (max 10MB to avoid localStorage/state limits)
    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage("O arquivo é muito grande. O tamanho limite é de 10MB.");
      return;
    }

    setErrorMessage(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Url = e.target?.result as string;
      if (!base64Url) return;

      const newFile: PatientFile = {
        id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        name: file.name,
        url: base64Url,
        type: file.type,
        size: formatBytes(file.size),
        uploadedAt: new Date().toISOString()
      };

      const updatedFiles = [...files, newFile];
      setFiles(updatedFiles);

      const updatedPatient: Patient = {
        ...patient,
        files: updatedFiles
      };
      onUpdatePatient(updatedPatient);
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
    };

    reader.onerror = () => {
      setErrorMessage("Falha ao ler o arquivo selecionado.");
    };

    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      Array.from(e.dataTransfer.files).forEach(file => {
        processFile(file as File);
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      Array.from(e.target.files).forEach(file => {
        processFile(file as File);
      });
    }
  };

  const onButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Tem certeza de que deseja excluir este arquivo?")) {
      const updatedFiles = files.filter(f => f.id !== id);
      setFiles(updatedFiles);

      const updatedPatient: Patient = {
        ...patient,
        files: updatedFiles
      };
      onUpdatePatient(updatedPatient);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) {
      return <ImageIcon className="text-teal-500" size={24} />;
    }
    if (type.includes('pdf')) {
      return <FileText className="text-rose-500" size={24} />;
    }
    return <File className="text-indigo-500" size={24} />;
  };

  const filteredFiles = files.filter(file => 
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-10 animate-in fade-in duration-500">
      
      {/* Upload Zone on Left (2 cols) */}
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
          <div>
            <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
              Upload de Documentos
            </h3>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
              Imagens, laudos, relatórios ou encaminhamentos
            </p>
          </div>

          <form 
            onDragEnter={handleDrag} 
            onDragOver={handleDrag} 
            onDragLeave={handleDrag} 
            onDrop={handleDrop}
            onSubmit={(e) => e.preventDefault()}
            className={`relative border-2 border-dashed rounded-[2rem] p-5 sm:p-10 flex flex-col items-center justify-center text-center transition-all ${dragActive ? 'border-indigo-600 bg-indigo-50/20 dark:bg-indigo-950/10 scale-[1.01]' : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 hover:border-indigo-400 dark:hover:border-indigo-800'}`}
          >
            <input 
              ref={fileInputRef}
              type="file" 
              multiple 
              onChange={handleChange}
              className="hidden" 
              accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            />
            
            <div className="p-4 bg-white dark:bg-slate-800 rounded-2xl shadow-md mb-4 text-slate-500 dark:text-slate-400">
              <Upload size={28} className="mx-auto text-indigo-600" />
            </div>

            <p className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-1">
              Arraste e solte o arquivo aqui
            </p>
            <p className="text-[10px] text-slate-400 font-bold mb-4">
              ou clique para selecionar do dispositivo
            </p>

            <button 
              type="button"
              onClick={onButtonClick}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md"
            >
              Procurar Arquivo
            </button>

            <p className="text-[9px] text-slate-400 mt-6 font-bold uppercase tracking-widest">
              PDF, Imagens, Word ou TXT até 10MB
            </p>
          </form>

          {errorMessage && (
            <div className="flex items-center gap-2 p-4 bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 rounded-2xl border border-rose-100 dark:border-rose-900/40 text-xs font-bold leading-tight">
              <AlertCircle size={18} />
              <p>{errorMessage}</p>
            </div>
          )}

          {uploadSuccess && (
            <div className="flex items-center gap-2 p-4 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 rounded-2xl border border-emerald-100 dark:border-emerald-900/40 text-xs font-black uppercase tracking-wider">
              <CheckCircle2 size={18} />
              <p>Arquivo anexado com sucesso!</p>
            </div>
          )}
        </div>
      </div>

      {/* Files List on Right (3 cols) */}
      <div className="lg:col-span-3 space-y-6">
        <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2rem] sm:rounded-[3.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-6 min-h-[500px] flex flex-col">
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b dark:border-slate-800 pb-5">
            <div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
                Repositório de Arquivos
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                Total de {files.length} arquivo(s) associado(s)
              </p>
            </div>

            {/* Search Input */}
            <div className="relative w-full sm:w-64">
              <input 
                type="text"
                placeholder="Buscar arquivo..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl pl-10 pr-4 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>
          </div>

          <div className="flex-1 space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
            {filteredFiles.map(file => (
              <div 
                key={file.id}
                className="p-5 bg-slate-50/50 dark:bg-slate-900/40 rounded-2xl border border-slate-100 dark:border-slate-800/80 hover:border-indigo-200 transition-all flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-4 overflow-hidden">
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-xl shadow-sm shrink-0">
                    {getFileIcon(file.type)}
                  </div>
                  <div className="overflow-hidden">
                    <p className="font-bold text-xs text-slate-800 dark:text-slate-200 truncate" title={file.name}>
                      {file.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider">
                        {file.size}
                      </span>
                      <span className="w-1 h-1 bg-slate-300 rounded-full" />
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                        {formatDate(file.uploadedAt)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <a 
                    href={file.url} 
                    download={file.name}
                    className="p-2.5 bg-white dark:bg-slate-800 hover:bg-slate-100 text-slate-600 dark:text-slate-300 rounded-xl shadow-sm hover:text-indigo-600 transition-all border dark:border-slate-700"
                    title="Baixar Arquivo"
                  >
                    <Download size={14} />
                  </a>
                  
                  {file.type.startsWith('image/') && (
                    <a 
                      href={file.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2.5 bg-white dark:bg-slate-800 hover:bg-slate-100 text-slate-600 dark:text-slate-300 rounded-xl shadow-sm hover:text-indigo-600 transition-all border dark:border-slate-700"
                      title="Visualizar Imagem"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}

                  <button 
                    onClick={() => handleDelete(file.id)}
                    className="p-2.5 bg-white dark:bg-slate-800 hover:bg-rose-50 dark:hover:bg-rose-950/20 text-slate-400 hover:text-rose-600 rounded-xl shadow-sm transition-all border dark:border-slate-700"
                    title="Excluir arquivo"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}

            {filteredFiles.length === 0 && (
              <div className="flex flex-col items-center justify-center text-center py-10 sm:py-20 bg-slate-50/20 dark:bg-slate-900/20 border border-dashed border-slate-200 dark:border-slate-800 rounded-[2.5rem] h-full min-h-[300px]">
                <File size={40} className="text-slate-300 dark:text-slate-700 mb-4" />
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Nenhum arquivo encontrado</p>
                <p className="text-[10px] text-slate-400 mt-1 font-bold">Arraste ou clique para fazer upload no painel esquerdo.</p>
              </div>
            )}
          </div>

        </div>
      </div>

    </div>
  );
};

export default PatientFiles;
